import axios from 'axios';
import api from './api';
import type { Claim } from '@/types';

/**
 * #401: the insurer Claims Desk (Phase 1). Backed by the insurer-only actions
 * on `ClaimViewSet` (`/claims/{id}/insurer/...`, `/claims/{id}/history/`,
 * `/claims/insurer/settings/`) plus `/claims/{id}/document-requests/`, which
 * the broker and the customer read too. The server is the authority on every
 * rule (graph, amount limit, audiences); the UI only mirrors it.
 */

/** The fields SPEC-401 8.2 adds to the insurer claim payload. */
export interface InsurerDeskClaim extends Omit<Claim, 'status'> {
  /** The full `Claim.STATUS_CHOICES` (the shared `ClaimStatus` type predates
   *  several of them, e.g. `assessment_pending`, `settlement_pending`). */
  status: string;
  insurer_claim_number?: string;
  forwarded_to_insurer_at?: string | null;
  is_cashless?: boolean;
  claimant_name?: string;
  diagnosis?: string;
  fir_number?: string;
  fir_date?: string | null;
  police_report_filed?: boolean;
  rejection_reason?: string;
  approved_at?: string | null;
  rejected_at?: string | null;
  insurer_decision?: '' | 'approved' | 'partially_approved' | 'rejected';
  insurer_decided_at?: string | null;
  insurer_decided_by_name?: string | null;
  open_document_requests_count?: number;
  can_edit?: boolean;
  can_decide?: boolean;
  /** null = unlimited (insurer admin); "0" = no limit configured. */
  my_decision_limit?: string | null;
  decision_within_limit?: boolean;
}

export type RemarkAudience = 'insurer_only' | 'broker' | 'customer';

export const AUDIENCE_LABELS: Record<RemarkAudience, string> = {
  insurer_only: 'Only my insurer',
  broker: 'Broker',
  customer: 'Broker and customer',
};

/** Who exactly reads a remark, in the words the modal shows. */
export const AUDIENCE_HINTS: Record<RemarkAudience, string> = {
  insurer_only: 'Only your insurer team will read this. The broker and the customer will not.',
  broker: 'Your team and the broker will read this. The customer will not.',
  customer: 'Your team, the broker and the customer will all read this.',
};

export interface ClaimHistoryEntry {
  id: string;
  created_at: string;
  action: string;
  action_display: string;
  description: string;
  author_side: 'system' | 'broker' | 'insurer' | 'customer';
  actor_name: string | null;
  from_status: string | null;
  to_status: string | null;
  amount: string | null;
  audience: RemarkAudience | null;
}

export interface ClaimDocumentRequest {
  id: string;
  document_type: string;
  document_type_display: string;
  description: string;
  requested_by_side: 'insurer' | 'broker';
  due_date: string | null;
  status: 'open' | 'fulfilled' | 'cancelled';
  status_display: string;
  fulfilled_by_document: string | null;
  fulfilled_at: string | null;
  cancelled_at: string | null;
  created_at: string;
}

export interface DocumentRequestItem {
  document_type: string;
  description?: string;
  due_date?: string | null;
}

export interface ClaimsDeskSettings {
  claims_approval_limit: string | null;
  my_decision_limit: string | null;
  can_change: boolean;
  can_edit: boolean;
  can_decide: boolean;
}

export interface SettleInput {
  gross_amount: string;
  tds_amount?: string;
  net_amount?: string;
  payment_mode: string;
  payment_reference: string;
  payment_date: string;
  remarks?: string;
}

/** The statuses whose next step is a decision, not a status change. */
export const DECISION_TARGETS = ['approved', 'partially_approved', 'rejected', 'settled'];

/** True when the API answered "the claim moved since you opened it" (409). */
export function isStaleClaimError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 409;
}

/** Mirrors `insurer_desk._awaiting_decision` -- advisory only. */
export function awaitingInsurerDecision(
  claim: Pick<InsurerDeskClaim, 'status' | 'insurer_decided_at'>
): boolean {
  if (claim.status === 'under_review' || claim.status === 'assessment_pending') return true;
  return claim.status === 'approved' && !claim.insurer_decided_at;
}

const claimUrl = (id: string) => `/claims/${id}/`;

export const claimsDeskService = {
  async history(claimId: string): Promise<ClaimHistoryEntry[]> {
    const response = await api.get(`${claimUrl(claimId)}history/`, { params: { page_size: 50 } });
    const body = response.data;
    return (Array.isArray(body) ? body : (body?.results ?? [])) as ClaimHistoryEntry[];
  },
  async documentRequests(claimId: string): Promise<ClaimDocumentRequest[]> {
    return (await api.get(`${claimUrl(claimId)}document-requests/`)).data;
  },
  async requestDocuments(
    claimId: string,
    items: DocumentRequestItem[],
    moveToDocumentsPending: boolean
  ): Promise<ClaimDocumentRequest[]> {
    return (
      await api.post(`${claimUrl(claimId)}document-requests/`, {
        items,
        move_to_documents_pending: moveToDocumentsPending,
      })
    ).data;
  },
  async cancelRequest(
    claimId: string,
    requestId: string,
    reason: string
  ): Promise<ClaimDocumentRequest> {
    return (
      await api.post(`${claimUrl(claimId)}document-requests/${requestId}/cancel/`, { reason })
    ).data;
  },
  async changeStatus(
    claimId: string,
    input: {
      status: string;
      expected_status: string;
      note?: string;
      note_audience?: RemarkAudience;
      insurer_claim_number?: string;
    }
  ): Promise<InsurerDeskClaim> {
    return (await api.post(`${claimUrl(claimId)}insurer/status/`, input)).data;
  },
  async addRemark(
    claimId: string,
    text: string,
    audience: RemarkAudience
  ): Promise<ClaimHistoryEntry> {
    return (await api.post(`${claimUrl(claimId)}insurer/remarks/`, { text, audience })).data;
  },
  async approve(
    claimId: string,
    input: {
      approved_amount: string;
      partial: boolean;
      remarks?: string;
      remarks_audience?: RemarkAudience;
      expected_status: string;
    }
  ): Promise<InsurerDeskClaim> {
    return (await api.post(`${claimUrl(claimId)}insurer/approve/`, input)).data;
  },
  async reject(claimId: string, reason: string, expectedStatus: string): Promise<InsurerDeskClaim> {
    return (
      await api.post(`${claimUrl(claimId)}insurer/reject/`, {
        reason,
        expected_status: expectedStatus,
      })
    ).data;
  },
  async settle(
    claimId: string,
    input: SettleInput & { expected_status: string }
  ): Promise<{ claim: InsurerDeskClaim }> {
    return (await api.post(`${claimUrl(claimId)}insurer/settle/`, input)).data;
  },
  /** #445: the broker's explicit send; idempotent on the server. */
  async sendToInsurer(
    claimId: string
  ): Promise<{ detail: string; forwarded_to_insurer_at: string; already_sent: boolean }> {
    return (await api.post(`${claimUrl(claimId)}send-to-insurer/`, {})).data;
  },
  async settings(): Promise<ClaimsDeskSettings> {
    return (await api.get('/claims/insurer/settings/')).data;
  },
  async updateLimit(limit: string | null): Promise<ClaimsDeskSettings> {
    return (await api.patch('/claims/insurer/settings/', { claims_approval_limit: limit })).data;
  },
};
